<template>
  <div class="organization-setup-view">
    <div class="organization-setup-view__body">
      <form v-if="currentStep == 'name'" @submit.prevent="saveName">
        <div class="text-center">
          <lucide-icon name="hand" class="organization-setup-view__icon organization-setup-view__hand" />
          <h1>{{ $t("headings.welcomeToOrganization") }}</h1>
        </div>

        <p>{{ $t("organizationOnboard.name") }}</p>
        <input-text
          :label="$t('labels.organizationName')"
          v-model:value="data.name"
          :required="true"
          :hide-errors="!data.triedSavingName"
          :error="data.nameError"
        />

        <div class="message-notice p-4 mt-2">
          <lucide-icon name="info" />
          {{ $t("organizationOnboard.nameHint") }}
        </div>

        <div class="flex mt-4">
          <button v-if="canGoBack" type="button" class="button button_secondary mr-auto" @click="goBack">
            {{ $t("labels.back") }}
          </button>

          <button v-if="canGoNext" type="submit" class="button ml-auto">
            {{ $t("actions.continue") }}
          </button>
        </div>
      </form>

      <section v-else-if="currentStep == 'users'">
        <div class="text-center">
          <lucide-icon name="mail" class="organization-setup-view__icon" />
          <h1>{{ $t("organizationOnboard.usersHeading") }}</h1>
        </div>
        <p>{{ $t("organizationOnboard.users") }}</p>
        <member-invitation-form
          ref="memberInvitationForm"
          class="p-0"
          :title="$t('actions.addMoreMembers')"
          :existing-emails="memberEmails"
          :invite-function="sendInvitations"
          :max-invitations="currentOrganization?.licenseLimit ?? Infinity"
          :roles="rolesUserCanAssign"
          :can-set-valid-until="true"
          :submit-button-visible="false"
          default-role-code="MEMBER"
        />

        <div class="flex mt-4">
          <button v-if="canGoBack" type="button" class="button button_secondary mr-auto" @click="goBack">
            {{ $t("labels.back") }}
          </button>

          <button v-if="canGoNext" type="button" class="button ml-auto" @click="inviteUsers()">
            <template v-if="usersFormUnfilled">
              {{ $t("actions.skip") }}
            </template>
            <template v-else>
              {{ $t("actions.inviteUsers") }}
            </template>
          </button>
        </div>
      </section>

      <section v-else-if="currentStep == 'teams'">
        <div class="text-center">
          <lucide-icon name="users" class="organization-setup-view__icon" />
          <h1>{{ $t("organizationOnboard.teamHeading") }}</h1>
        </div>
        <p>{{ $t("organizationOnboard.team") }}</p>
        <team-create-form
          ref="teamCreateForm"
          :organization-slug="organizationSlug"
          :submit-button-visible="false"
          @create="onOrganizationTeamCreate"
        />

        <div class="flex mt-4">
          <button v-if="canGoBack" type="button" class="button button_secondary mr-auto" @click="goBack">
            {{ $t("labels.back") }}
          </button>

          <button v-if="canGoNext" type="button" class="button ml-auto" @click="createTeam">
            <template v-if="!teamNameFilled">
              {{ $t("actions.skip") }}
            </template>
            <template v-else>
              {{ $t("actions.createTeam") }}
            </template>
          </button>
        </div>
      </section>

      <section v-else-if="currentStep == 'done'">
        <div class="text-center">
          <lucide-icon name="party-popper" class="organization-setup-view__icon" />
          <h1>{{ $t("organizationOnboard.doneHeading") }}</h1>
          <p style="text-wrap: balance">{{ $t("organizationOnboard.done") }}</p>
        </div>

        <div class="flex mt-6">
          <button v-if="canGoBack" type="button" class="button mr-auto ml-auto" @click="finalizeSetup()">
            {{ $t("labels.done") }}
          </button>
        </div>
      </section>
    </div>
  </div>
</template>

<script setup lang="ts">
import { reactive, watch, ref, computed, useTemplateRef } from 'vue';
import { useUser } from '@/api/user.store';
import { useI18n } from 'vue-i18n';
import { useRoute, useRouter } from 'vue-router';
import { useOrganizations } from './organizations.store';

import LucideIcon from '@core/ui/LucideIcon.vue';
import InputText from '@core/input/InputText.vue';
import MemberInvitationForm from '@manager/members/MemberInvitationForm.vue';
import TeamCreateForm from '@manager/teams/TeamCreateForm.vue';

import { showError } from '@core/ui/alert/alert';
import { useToast } from 'vue-toastification';
import { BadRequestError } from '@core/errors/Errors';
import { notNull } from '@/helpers/utility/Type';

import type { OrganizationRole } from '@/api/Organization';

const props = defineProps<{
  organizationSlug: string;
}>();

const router = useRouter();
const route = useRoute();
const toast = useToast();
const { t } = useI18n();
const { user } = useUser();
const {
  currentOrganization,
  inviteOrganizationMembers,
  updateOrganization
} = useOrganizations(props, { user });

// =======================
// ORGANIZATION NAME

const data = reactive({
  name: "",
  nameError: "",
  triedSavingName: false
});

watch(currentOrganization, (organization) => {
  if (organization) {
    if (isDefaultName(organization.name)) {
      data.name = "";
    } else {
      data.name = organization.name;
    }
  } else {
    data.name = "";
  }
}, { immediate: true });

function isDefaultName(name: string) {
  return name.startsWith(t('texts.defaultOrganizationName', { name: user.value?.firstname }));
}

async function saveName() {
  if (!currentOrganization.value) {
    showError("Organization not found");
    throw new Error("Organization not found");
  }
  data.nameError = "";
  data.triedSavingName = true;
  if (data.name == currentOrganization.value.name) {
    goToNext();
    return;
  }

  try {
    const slugBefore = currentOrganization.value.slug;
    const next = nextStep.value ?? "users";
    const updatedOrg = await updateOrganization(props.organizationSlug, { name: data.name, slug: slugify(data.name) });
    if (updatedOrg.slug != slugBefore) {
      // Redirect to new slug
      await router.replace({
        name: "settings-organization",
        params: {
          organizationSlug: updatedOrg.slug
        },
        query: {
          setupstep: next
        }
      });
    }

    toast.success(t('texts.organizationNameUpdated'));
    goToNext();
  } catch (e) {
    const error = e as BadRequestError;
    if (error.fields?.name) {
      data.nameError = error.fields.name;
    } else if (error.fields?.slug) {
      data.nameError = error.fields.slug;
    } else {
      showError(error);
    }
    return;
  }
}

function slugify(name: string) {
  return name.toLowerCase().replace(/[^a-z0-9]/g, "-").replace(/-+/g, "-");
}

// =======================
// MEMBERS

const memberInvitationForm = useTemplateRef("memberInvitationForm");

const members = computed(() => {
  const allMembers = currentOrganization.value?.members ?? [];
  return allMembers.sort((a, b) => {
    if (a.created < b.created) {
      return -1;
    }
    if (a.created > b.created) {
      return 1;
    }
    return 0;
  });
});

const memberEmails = computed(() => {
  return members.value.map((m) => m.email).filter(notNull);
});

async function sendInvitations(entries: { user: { email: string }; roleCode?: string }[]) {
  if (!currentOrganization.value) {
    showError("Organization not found");
    throw new Error("Organization not found");
  }
  return await inviteOrganizationMembers(currentOrganization.value, entries);
}

const usersFormUnfilled = computed((): boolean => {
  const memberForm = memberInvitationForm.value;
  if (!memberForm) {
    return false;
  }
  const emailInput = memberForm.emailInput;
  if (!emailInput) {
    return false;
  }
  return emailInput.empty && memberForm.data.entries.length === 0;
});

async function inviteUsers() {
  const form = memberInvitationForm.value;
  if (!form) {
    return;
  }
  if (usersFormUnfilled.value === true) {
    goToNext();
    return;
  } else {
    console.log(form.data.entries, form.emailInput);
  }

  const success = await form.submit();
  if (success) {
    toast.success(t('texts.invitationsSent'));
    goToNext();
  }
}

const roles = computed(() => {
  const rolesArray = currentOrganization.value?.roles || [];
  const roles: Record<string, OrganizationRole> = {};
  for (const role of rolesArray) {
    roles[role.roleCode] = role;
  }
  return roles;
});

const assignableRoles = computed(() => {
  return Object.values(roles.value)
    .map((r) => r.roleCode)
    .filter((roleCode) => !["OWNER", "ASSISTANT_OWNER"].includes(roleCode));
});

const rolesUserCanAssign = computed(() => {
  return Object.values(roles.value).filter((r) =>
    assignableRoles.value.includes(r.roleCode)
  );
});

// =======================
// TEAM

const teamCreateForm = useTemplateRef("teamCreateForm");

const teamNameFilled = computed((): boolean => {
  return teamCreateForm.value?.data.name.trim() !== "";
});

const hasCreatedTeam = ref(false);

async function createTeam() {
  if (!currentOrganization.value) {
    showError("Organization not found");
    throw new Error("Organization not found");
  }
  if (!teamNameFilled.value) {
    goToNext();
    return;
  }
  if (!teamCreateForm.value) {
    return;
  }
  const success = await teamCreateForm.value.submit();
  if (success) {
    toast.success(t('texts.teamCreated'));
    goToNext();
  }
}

function onOrganizationTeamCreate() {
  hasCreatedTeam.value = true;
}

// =======================
// FINALIZE

async function finalizeSetup() {
  if (!currentOrganization.value) {
    showError("Organization not found");
    throw new Error("Organization not found");
  }
  const completionDate = new Date().toISOString();
  await updateOrganization(props.organizationSlug, { setupCompletedAt: completionDate });
  toast.success(t('texts.organizationSetupComplete'));
}

// =======================
// NAVIGATION

const steps = computed(() => {
  if (currentOrganization.value?.licenseLimit == null) {
    return [{
      id: "name"
    }, {
      id: "teams",
    }, {
      id: "done"
    }];
  }
  if (currentOrganization.value.licenseLimit <= 1) {
    return [
      {
        id: "name"
      },
      {
        id: "teams",
      },
      {
        id: "done"
      }
    ];
  }
  return [
    {
      id: "name"
    },
    {
      id: "users"
    },
    {
      id: "teams"
    },
    {
      id: "done"
    }
  ];
});
const currentStep = ref<string>((route.query.setupstep as string | null) ?? "name");
const nextStep = computed(() => {
  const currentIndex = steps.value.findIndex(step => step.id === currentStep.value);
  if (currentIndex < steps.value.length - 1) {
    return steps.value[currentIndex + 1].id;
  }
  return null;
});
const prevStep = computed(() => {
  const currentIndex = steps.value.findIndex(step => step.id === currentStep.value);
  if (currentIndex > 0) {
    return steps.value[currentIndex - 1].id;
  }
  return null;
});

function goBack() {
  const currentIndex = steps.value.findIndex(step => step.id === currentStep.value);
  if (currentIndex > 0) {
    currentStep.value = steps.value[currentIndex - 1].id;
  }
}

function goToNext() {
  const currentIndex = steps.value.findIndex(step => step.id === currentStep.value);
  if (currentIndex < steps.value.length - 1) {
    currentStep.value = steps.value[currentIndex + 1].id;
  }
  console.log("go to index", currentStep.value);
}

const canGoBack = computed(() => prevStep.value != null);
const canGoNext = computed(() => nextStep.value != null);
</script>

<style lang="scss">
.organization-setup-view {
  display: flex;
  flex-direction: column;
  height: 100%;
  align-items: center;
  justify-content: center;

  &__body {
    border-radius: 1rem;
    padding: 2rem;
    background-color: var(--color-background-deep);
    width: 50rem;
    max-width: 100%;
  }

  &__icon {
    font-size: 3rem;
    color: var(--color-text-medium);
  }

  &__hand {
    animation: hand-wave 2s infinite;
    animation-timing-function: ease-in-out;
    transform-origin: 50% 100%;
  }

  @keyframes hand-wave {
    0% {
      transform: rotate(10deg);
    }
    50% {
      transform: rotate(-10deg);
    }
    100% {
      transform: rotate(10deg);
    }
  }
}
</style>